import React, { useState } from "react";
import { useRoleVerify } from "Common/RoleVerify";
import { 
  getCurrencies,
  deleteCurrency 
} from "adminApi/CurrencyListApi/CurrencyList";
import DataTable from "Common/DataTable/DataTable";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import usePaginatedFetch from "Hooks/usePaginatedFetch";
import useTableConfig from "Hooks/useTableConfig";
import { generateDisplayFields } from "utils/fieldMapper";
import fields from "../../json/CurrencyFields.json";

/**
 * CurrencyListTable Component
 * Displays a paginated list of system currencies with management actions.
 */
const CurrencyListTable = ({ refresh }) => {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search, 500);
  
  // Role-based permission check
  const canShowAction = useRoleVerify("", ["can_edit", "can_delete"]);
  const sizeOptions = [10, 20, 50, 100];
  const { tabledata } = fields;

  /**
   * Fetch Paginated Data
   * Hooks into the API to retrieve current currencies based on search and pagination.
   */
  const { data, meta, loading, error, refetch } = usePaginatedFetch(getCurrencies, {
    page,
    limit,
    search: debouncedSearch,
    title: "System Currency List",
    refresh,
  });

  // Automatically generate standard display fields from JSON
  const displayFields = generateDisplayFields(tabledata);

  // Table Configuration Hook
  const { formattedData, columns } = useTableConfig({
    data,
    page,
    limit,
    canShowAction,
    refetch,
    config: {
      entityName: "Currency",
      editUrl: "currency-edit",
      idKey: "currencyid",
      deleteApi: deleteCurrency,
      displayFields,
    },
  });

  const handleSearch = (val, resetPage = null) => {
    setSearch(val);
    if (resetPage !== null) setPage(resetPage);
  };

  const onPageChange = (p) => setPage(p);

  const onLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  return (
    <DataTable
      columns={columns}
      data={formattedData}
      options={{
        loading,
        error,
        sortableColumns: displayFields.map((f) => f.accessor),
        copy: true,
        csv: true,
        pdf: true,
        print: true,
        fileName: "System_Currency_List",
      }}
      Search={{
        search: true,
        SearchInputValue: search,
        handleSearch: handleSearch,
      }}
      Entries={{
        Entries: true,
        limit,
        onLimitChange,
        options: sizeOptions,
      }}
      pagination={{
        isActive: true,
        meta,
        onPageChange,
      }}
    />
  );
};

export default CurrencyListTable;
